import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useTheme,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/DataTable";
import { motion } from "framer-motion";
import Input from "../../components/Input";
import { authService } from "../../services/auth/authService";
import { User, UserResponse } from "../../types/api.types";
import { formatDate } from "../../utils/date";
import { userService } from "../../services/user/userService";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "../../context/SnackbarContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const PAGE_SIZE = 10;
function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;

  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";

  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

const UserPage = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(0);

  const { showSnackbar } = useSnackbar();

  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
  const [isLoadingEnable, setIsLoadingEnable] = useState<boolean>(false);

  const [selectedUserToDelete, setSelectedUserToDelete] = useState<User | null>(
    null,
  );
  const [selectedUserToEnable, setSelectedUserToEnable] = useState<User | null>(
    null,
  );

  const theme = useTheme();

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEnableModal, setShowEnableModal] = useState<boolean>(false);

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [search, setSearch] = useState("");

  const columns = [
    {
      header: "Nombre",
      key: "name",
    },
    {
      header: "Email",
      key: "email",
    },

    {
      header: "Rol",
      key: "rol",
      render: (row: any) => {
        return (
          <Chip
            label={row?.rol}
            size="small"
            sx={{
              backgroundColor: theme.palette.secondary.light + "30",
              color: theme.palette.primary.main,
              fontWeight: "600",
              border: `1px solid ${theme.palette.primary.main}30`,
              borderRadius: 2,
            }}
          />
        );
      },
    },

    {
      header: "Cuit",
      key: "cuit",
    },
    {
      header: "Estado",
      key: "visible",
      render: (value: any) => {
        return (
          <Chip
            label={value?.visible ? "Activo" : "Inactivo"}
            size="small"
            sx={{
              backgroundColor: value?.visible
                ? theme.palette.primary.main + "20"
                : theme.palette.error.main + "20",
              color: value?.visible
                ? theme.palette.primary.main
                : theme.palette.error.main,
              fontWeight: "600",
              border: `1px solid ${
                value?.visible
                  ? theme.palette.primary.main + "40"
                  : theme.palette.error.main + "40"
              }`,
              borderRadius: 2,
            }}
          />
        );
      },
    },
    {
      header: "Fecha de creación",
      key: "createDate",
      render: (value: any) => (
        <Typography>{formatDate(value?.createDate)}</Typography>
      ),
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <EditIcon
            sx={{ cursor: "pointer", color: "primary.main" }}
            onClick={() => navigate(`/users/edit/${row.userId}`)}
          />
          {!row?.visible ? (
            <VisibilityIcon
              sx={{ cursor: "pointer", color: "error.main" }}
              onClick={() => onEnable(row)}
            />
          ) : (
            <VisibilityOffIcon
              sx={{ cursor: "pointer", color: "primary.main" }}
              onClick={() => onDelete(row)}
            />
          )}
        </Box>
      ),
    },
  ];

  const [users, setUsers] = useState<User[]>();

  const getUsers = async () => {
    setIsLoading(true);
    const response = await userService.getUsers();
    setUsers(response);
    setIsLoading(false);
  };

  useEffect(() => {
    getUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users || [];

    const value = search.toLowerCase().trim();

    return (users || []).filter(
      (user) =>
        user.name?.toLowerCase().includes(value) ||
        user.email?.toLowerCase().includes(value) ||
        user.cuit?.toString().includes(value),
    );
  }, [users, search]);

  const sortedUsers = useMemo(() => {
    if (!sortColumn) return filteredUsers;

    const dir = sortDirection === "asc" ? 1 : -1;

    return [...filteredUsers].sort(
      (a: any, b: any) => compareValues(a[sortColumn], b[sortColumn]) * dir,
    );
  }, [filteredUsers, sortColumn, sortDirection]);

  const paginatedUsers = useMemo(() => {
    return sortedUsers.slice(
      currentPage * PAGE_SIZE,
      (currentPage + 1) * PAGE_SIZE,
    );
  }, [sortedUsers, currentPage]);

  //   const paginatedData = sortedTableData.slice(
  //   (reportPage - 1) * PAGE_SIZE,
  //   reportPage * PAGE_SIZE,
  // );

  const handleSortChange = (key: string) => {
    if (!key) {
      setSortColumn(null);
      setSortDirection("asc");
      return;
    }

    if (sortColumn === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(key);
      setSortDirection("asc");
    }

    setCurrentPage(0);
  };

  const onDelete = async (user: User) => {
    setShowDeleteModal(true);
    setSelectedUserToDelete(user);
  };

  const onEnable = async (user: User) => {
    setShowEnableModal(true);
    setSelectedUserToEnable(user);
  };

  const onDeleteUser = async () => {
    setIsLoadingDelete(true);
    try {
      const response = await userService.deactivateUser(
        selectedUserToDelete?.userId || "",
      );
      showSnackbar(
        response?.message || "Usuario inhabilitado correctamente",
        "success",
        3000,
      );

      setShowDeleteModal(false);
      if (currentPage === 0) {
        getUsers();
      } else {
        setCurrentPage(0);
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al inhabilitar el usuario",
        "error",
        4000,
      );
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const onEnableUser = async () => {
    setIsLoadingEnable(true);
    try {
      const response = await userService.activateUser(
        selectedUserToEnable?.userId || "",
      );
      showSnackbar(
        response?.message || "Usuario habilitado correctamente",
        "success",
        3000,
      );
      if (currentPage === 0) {
        getUsers();
      } else {
        setCurrentPage(0);
      }
      setShowEnableModal(false);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al habilitar el usuario",
        "error",
        4000,
      );
    } finally {
      setIsLoadingEnable(false);
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h4">Usuarios</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/users/create")}
              >
                Crear Usuario
              </Button>
            </Box>
          </motion.div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              mt: 2,
            }}
          >
            <TextField
              label="Buscar usuario"
              placeholder="Nombre, email o CUIT"
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(0);
              }}
              sx={{ width: 350 }}
            />
          </Box>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            style={{ marginTop: 2 }}
          >
            <DataTable
              isLoading={isLoading}
              columns={columns}
              total={filteredUsers.length}
              data={paginatedUsers}
              page={currentPage + 1}
              rowsPerPage={PAGE_SIZE}
              sortable
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              onChange={(page) => {
                setCurrentPage(page - 1);
              }}
            />
          </motion.div>
        </CardContent>
      </Card>
      <Dialog
        open={showDeleteModal}
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Estás seguro de querer inhabilitar este usuario?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Se inhabilitará el usuario{" "}
            <strong style={{ color: "primary.main" }}>
              {selectedUserToDelete?.name}
            </strong>{" "}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isLoadingDelete}
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedUserToDelete(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={onDeleteUser}
            autoFocus
            disabled={isLoadingDelete}
            loading={isLoadingDelete}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showEnableModal}
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Estás seguro de querer habilitar este usuario?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Se habilitará el usuario{" "}
            <strong style={{ color: "primary.main" }}>
              {selectedUserToEnable?.name}
            </strong>{" "}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isLoadingEnable}
            onClick={() => {
              setShowEnableModal(false);
              setSelectedUserToEnable(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={onEnableUser}
            autoFocus
            disabled={isLoadingEnable}
            loading={isLoadingEnable}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPage;
